"use client";

import { useState, useEffect } from "react";
import { loadStripe } from "@stripe/stripe-js";
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from "@stripe/react-stripe-js";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { AlertCircle, CheckCircle2 } from "lucide-react";
import { PLAN_DISPLAY } from "@/lib/feature-display";

const stripePromise = process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY
  ? loadStripe(process.env.NEXT_PUBLIC_STRIPE_PUBLISHABLE_KEY)
  : null;

interface EmbeddedSubscribeCardProps {
  planId: "monthly" | "annual";
  triggerLabel?: string;
  triggerClassName?: string;
  onSubscribed?: () => void;
  /** Fires when the trigger button is clicked, before the dialog opens (e.g. for analytics). */
  onTriggerClick?: () => void;
}

function SubscribeForm({
  planId,
  setupIntentId,
  onSucceeded,
}: {
  planId: "monthly" | "annual";
  setupIntentId: string;
  onSucceeded: () => void;
}) {
  const stripe = useStripe();
  const elements = useElements();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  // Set once confirmSetup succeeds. A retry after a failed /finalize call
  // (network blip, rate limit) must skip confirmSetup — the SetupIntent is
  // already "succeeded" and Stripe rejects confirming it a second time —
  // and just re-POST /finalize, which is now safe to repeat thanks to the
  // idempotency key on subscription creation.
  const [cardConfirmed, setCardConfirmed] = useState(false);

  const finalize = async () => {
    const res = await fetch("/api/billing/embedded/finalize", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ planId, setupIntentId }),
    });
    const data = (await res.json().catch(() => ({}))) as { error?: string };
    if (!res.ok) {
      throw new Error(data.error || "Failed to complete subscription");
    }
    onSucceeded();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    setIsLoading(true);
    setError(null);

    try {
      if (!cardConfirmed) {
        if (!stripe || !elements) return;

        const result = await stripe.confirmSetup({
          elements,
          confirmParams: {
            return_url: `${window.location.origin}/billing/complete?plan=${planId}`,
          },
          redirect: "if_required",
        });

        if (result.error) {
          setError(result.error.message || "Card setup failed");
          return;
        }

        if (result.setupIntent?.status !== "succeeded") {
          setError("Card setup did not complete. Please try again.");
          return;
        }

        setCardConfirmed(true);
      }

      await finalize();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Something went wrong");
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <PaymentElement />

      {error && (
        <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          <span>{error}</span>
        </div>
      )}

      <p className="text-xs text-gray-500">
        Your card is saved but not charged today. Your 30-day free trial
        starts now — cancel anytime before it ends and you won&apos;t be billed.
      </p>

      <Button
        type="submit"
        disabled={(!cardConfirmed && !stripe) || isLoading}
        className="w-full"
      >
        {isLoading
          ? "Starting your trial…"
          : cardConfirmed
            ? "Try Again"
            : "Start Free Trial"}
      </Button>
    </form>
  );
}

export function EmbeddedSubscribeCard({
  planId,
  triggerLabel = "Subscribe Now",
  triggerClassName,
  onSubscribed,
  onTriggerClick,
}: EmbeddedSubscribeCardProps) {
  const [open, setOpen] = useState(false);
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [setupIntentId, setSetupIntentId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [alreadySubscribed, setAlreadySubscribed] = useState(false);
  const [succeeded, setSucceeded] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    setLoading(true);
    setError(null);
    setAlreadySubscribed(false);

    (async () => {
      try {
        const res = await fetch("/api/billing/embedded/create-intent", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ planId }),
        });
        const data = (await res.json().catch(() => ({}))) as {
          error?: string;
          alreadySubscribed?: boolean;
          clientSecret?: string;
          setupIntentId?: string;
        };
        if (cancelled) return;

        if (res.status === 409 && data.alreadySubscribed) {
          setAlreadySubscribed(true);
          return;
        }
        if (!res.ok || !data.clientSecret || !data.setupIntentId) {
          throw new Error(data.error || "Failed to start subscription setup");
        }
        setClientSecret(data.clientSecret);
        setSetupIntentId(data.setupIntentId);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : "Failed to start subscription setup");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [open, planId, retryKey]);

  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen) {
      onTriggerClick?.();
    } else {
      setClientSecret(null);
      setSetupIntentId(null);
      setError(null);
      setSucceeded(false);
    }
  };

  const handleManageBilling = async () => {
    const res = await fetch("/api/billing/portal", { method: "POST" });
    const data = (await res.json().catch(() => ({}))) as { url?: string };
    if (res.ok && data.url) window.location.href = data.url;
  };

  const price = planId === "monthly" ? PLAN_DISPLAY.monthlyPrice : PLAN_DISPLAY.annualPrice;
  const interval = planId === "monthly" ? "month" : "year";

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogTrigger asChild>
        <Button className={triggerClassName}>{triggerLabel}</Button>
      </DialogTrigger>
      <DialogContent className="w-[95vw] max-w-[450px] mx-auto">
        <DialogHeader>
          <DialogTitle>Subscribe to {PLAN_DISPLAY.name}</DialogTitle>
          <DialogDescription>
            30-day free trial, then ${price}/{interval}, renewing automatically every {interval} until you cancel. Cancel anytime from the Billing page.
          </DialogDescription>
        </DialogHeader>

        {succeeded ? (
          <div className="text-center py-6">
            <CheckCircle2 className="mx-auto h-10 w-10 text-green-600 mb-3" />
            <p className="font-medium mb-1">You&apos;re subscribed!</p>
            <p className="text-sm text-gray-500 mb-4">Your 30-day free trial has started.</p>
            <Button
              onClick={() => {
                setOpen(false);
                onSubscribed?.();
              }}
              className="w-full"
            >
              Continue
            </Button>
          </div>
        ) : alreadySubscribed ? (
          <div className="py-2">
            <p className="text-sm text-gray-600 mb-4">
              You already have an active subscription.
            </p>
            <DialogFooter>
              <Button onClick={handleManageBilling} className="w-full sm:w-auto">
                Manage Billing
              </Button>
            </DialogFooter>
          </div>
        ) : loading ? (
          <div className="text-center py-8 text-gray-500">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto mb-2" />
            Loading…
          </div>
        ) : error ? (
          <div className="py-2">
            <div className="flex items-center gap-2 text-red-600 text-sm bg-red-50 p-3 rounded mb-4">
              <AlertCircle className="h-4 w-4 flex-shrink-0" />
              <span>{error}</span>
            </div>
            <Button onClick={() => setRetryKey((k) => k + 1)} className="w-full">
              Try Again
            </Button>
          </div>
        ) : clientSecret && !stripePromise ? (
          <div className="flex items-center gap-2 text-amber-600 text-sm bg-amber-50 p-3 rounded">
            <AlertCircle className="h-4 w-4 flex-shrink-0" />
            <span>Payment setup is not configured. Please contact support.</span>
          </div>
        ) : clientSecret && setupIntentId && stripePromise ? (
          <Elements
            stripe={stripePromise}
            options={{
              clientSecret,
              appearance: { theme: "stripe", variables: { colorPrimary: "#9333ea" } },
            }}
          >
            <SubscribeForm
              planId={planId}
              setupIntentId={setupIntentId}
              onSucceeded={() => setSucceeded(true)}
            />
          </Elements>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
